import { useCallback, useRef, useState } from 'react';

/**
 * Custom hook for tracking changes and status across multiple cards identified by locationId and productId.
 * Manages card refs, change state, and status state for dynamic card collections.
 *
 * @returns {Object} Card tracking utilities
 * @returns {Object} cardRefs - Ref object containing refs for each card
 * @returns {Function} getCardRef - Function to get or create a ref for a specific card
 * @returns {Function} handleCardChange - Function to update change state for a specific card
 * @returns {Function} getCardHasChanges - Function to check if a specific card has changes
 * @returns {Function} setCardStatus - Function to set the status for a specific card
 * @returns {Function} getCardStatus - Function to get the status of a specific card
 *
 * @example
 * const { cardRefs, getCardRef, handleCardChange, getCardHasChanges, setCardStatus, getCardStatus } = useCardChangeTracking();
 *
 * // Get a ref for a specific card
 * const cardRef = getCardRef(locationId, productId);
 *
 * // Mark a card as having changes
 * handleCardChange(locationId, productId, true);
 *
 * // Check if a card has changes
 * const hasChanges = getCardHasChanges(locationId, productId);
 *
 * // Set card status
 * setCardStatus(locationId, productId, CARD_STATUS.PROGRESS);
 *
 * // Get card status
 * const status = getCardStatus(locationId, productId);
 */
export default function useCardChangeTracking() {
  const [cardChanges, setCardChanges] = useState({});
  const [cardStatuses, setCardStatuses] = useState({});
  const cardRefs = useRef({});

  /**
   * Get or create a ref for a specific card
   * @param {number} locationId - The location identifier
   * @param {number} productId - The product identifier
   * @returns {React.RefObject} The ref for the specified card
   */
  const getCardRef = useCallback((locationId, productId) => {
    const cardKey = `${locationId}-${productId}`;
    if (!cardRefs.current[cardKey]) {
      cardRefs.current[cardKey] = { current: null };
    }
    return cardRefs.current[cardKey];
  }, []);

  /**
   * Update the change state for a specific card
   * @param {number} locationId - The location identifier
   * @param {number} productId - The product identifier
   * @param {boolean} hasChanges - Whether the card has changes
   */
  const handleCardChange = useCallback((locationId, productId, hasChanges) => {
    const cardKey = `${locationId}-${productId}`;
    setCardChanges(prev => ({
      ...prev,
      [cardKey]: hasChanges,
    }));
  }, []);

  /**
   * Check if a specific card has changes
   * @param {number} locationId - The location identifier
   * @param {number} productId - The product identifier
   * @returns {boolean} Whether the card has changes
   */
  const getCardHasChanges = useCallback(
    (locationId, productId) => {
      const cardKey = `${locationId}-${productId}`;
      return cardChanges[cardKey] || false;
    },
    [cardChanges]
  );

  /**
   * Set the status for a specific card
   * @param {number} locationId - The location identifier
   * @param {number} productId - The product identifier
   * @param {string|null} status - The status to set (CARD_STATUS.PROGRESS, CARD_STATUS.SUCCESS, CARD_STATUS.ERROR, or null)
   */
  const setCardStatus = useCallback((locationId, productId, status) => {
    const cardKey = `${locationId}-${productId}`;
    setCardStatuses(prev => ({
      ...prev,
      [cardKey]: status,
    }));
  }, []);

  /**
   * Get the status of a specific card
   * @param {number} locationId - The location identifier
   * @param {number} productId - The product identifier
   * @returns {string|null} The card status or null
   */
  const getCardStatus = useCallback(
    (locationId, productId) => {
      const cardKey = `${locationId}-${productId}`;
      return cardStatuses[cardKey] || null;
    },
    [cardStatuses]
  );

  return {
    cardRefs,
    getCardHasChanges,
    getCardRef,
    getCardStatus,
    handleCardChange,
    setCardStatus,
  };
}
